import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { calculatePricing, validatePricing, PRICING_VERSION } from '../utils/pricingEngine';
import { 
  Building2, 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  CreditCard, 
  Landmark, 
  Globe,
  Calculator,
  Users,
  User,
  Home,
  TrendingUp,
  Briefcase,
  UserCircle,
  Megaphone,
  Palmtree,
  AlertTriangle
} from 'lucide-react';
import ExitIntentPopup from './ExitIntentPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const [currentStep, setCurrentStep] = useState(1);
  const [orgId, setOrgId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [emailValidation, setEmailValidation] = useState({ checking: false, available: null, message: '' });
  
  // Email availability check with debounce
  const checkEmailAvailability = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailValidation({ checking: false, available: null, message: '' });
      return;
    }
    
    setEmailValidation({ checking: true, available: null, message: 'Checking...' });
    
    try {
      const response = await axios.get(`${API}/auth/check-email/${encodeURIComponent(email)}`);
      setEmailValidation({
        checking: false,
        available: response.data.available,
        message: response.data.message
      });
    } catch (error) {
      console.error('Email check error:', error);
      setEmailValidation({ checking: false, available: null, message: '' });
    }
  };
  
  // Save progress whenever step changes
  const saveProgress = async (step, data = null) => {
    if (!orgId) return;
    try {
      await axios.post(`${API}/onboarding/${orgId}/save-progress`, {
        current_step: step,
        form_data: data || formData
      });
      // Also save to localStorage as backup
      localStorage.setItem('onboarding_progress', JSON.stringify({
        orgId,
        step,
        formData: data || formData,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };
  
  // Load saved progress on mount
  useEffect(() => {
    const loadSavedProgress = async () => {
      // Check localStorage first
      const savedProgress = localStorage.getItem('onboarding_progress');
      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);
          // Only restore if less than 24 hours old
          const savedTime = new Date(progress.timestamp);
          const hoursSince = (new Date() - savedTime) / (1000 * 60 * 60);
          
          if (hoursSince < 24 && progress.orgId) {
            setOrgId(progress.orgId);
            setCurrentStep(progress.step || 1);
            if (progress.formData) {
              setFormData(prev => ({ ...prev, ...progress.formData }));
            }
            toast.info('Welcome back! Resuming from where you left off.');
          }
        } catch (e) {
          console.error('Failed to parse saved progress:', e);
        }
      }
    };
    
    loadSavedProgress();
  }, []);
  
  useEffect(() => {
    // Check if returning from Stripe payment
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const step = urlParams.get('step');
    const error = urlParams.get('error');
    
    if (error) {
      // Payment error from Stripe
      setPaymentError(decodeURIComponent(error));
      toast.error(`Payment failed: ${decodeURIComponent(error)}`);
      setCurrentStep(6);
    } else if (sessionId && step === 'payment-success') {
      handlePaymentReturn(sessionId);
    } else if (step === 'payment-cancelled') {
      toast.error('Payment was cancelled. Please try again.');
      setPaymentError('Payment was cancelled by user.');
      setCurrentStep(6); // Go back to pricing step
    }
  }, []);

  const handlePaymentReturn = async (sessionId) => {
    setLoading(true);
    toast.info('Verifying payment...');
    
    // Retrieve orgId from localStorage
    const storedOrgId = localStorage.getItem('onboarding_org_id');
    if (!storedOrgId) {
      toast.error('Session expired. Please start onboarding again.');
      setLoading(false);
      navigate('/onboarding');
      return;
    }
    
    // Update component state
    setOrgId(storedOrgId);
    
    // Poll for payment status
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds
    
    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        toast.error('Payment verification timed out. Please contact support.');
        setLoading(false);
        return;
      }
      
      const status = await checkPaymentStatus(sessionId);
      
      if (status && status.payment_status === 'paid') {
        toast.success('Payment confirmed! Completing your registration...');
        // Now complete onboarding with the stored orgId
        await completeOnboardingWithOrgId(storedOrgId);
      } else if (status && status.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setLoading(false);
        setCurrentStep(6);
      } else {
        // Continue polling
        attempts++;
        setTimeout(pollStatus, pollInterval);
      }
    };
    
    pollStatus();
  };

  const completeOnboardingWithOrgId = async (orgIdToUse) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/onboarding/${orgIdToUse}/complete`);
      
      // Clear stored orgId
      localStorage.removeItem('onboarding_org_id');
      
      // Store auth data if returned
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user_role', response.data.role);
        localStorage.setItem('user_id', response.data.user_id);
        
        toast.success('Account created successfully! Redirecting to your dashboard...');
        
        // Navigate to role-specific dashboard
        navigate(response.data.dashboard_url || '/dashboard');
      } else {
        toast.success('Onboarding completed successfully!');
        navigate('/');
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      if (error.response?.status === 402) {
        toast.error('Payment required to complete onboarding');
      } else {
        toast.error('Failed to complete onboarding: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Form data state
  // Handle URL query params for pre-selected role
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const roleParam = urlParams.get('role');
    
    if (roleParam && !formData.org_type) {
      // Map query param to org_type
      const roleMapping = {
        'vacation_rental': 'vacation_rental',
        'landlord': 'landlord',
        'hoa': 'hoa',
        'property_manager': 'property_manager'
      };
      
      if (roleMapping[roleParam]) {
        setFormData(prev => ({
          ...prev,
          org_type: roleMapping[roleParam]
        }));
      }
    }
  }, [location.search]);

  const [formData, setFormData] = useState({
    // Step 1: Organization Type
    org_type: location.state?.selectedType || '',
    country: 'US', // Default to United States
    org_name: '',
    
    // Step 2: Ownership Type
    ownership_type: '',
    
    // Step 3: Payment Details - Enhanced for Wise API
    payment_preference: 'payout', // 'payout', 'existing_merchant', or 'need_merchant'
    has_merchant_account: false,
    bank_name: '',
    custom_bank_name: '',
    bank_address: '',
    branch_code: '',
    swift_code: '',
    account_number: '',
    account_holder_name: '',
    iban: '',
    routing_number: '',
    sort_code: '',
    merchant_id: '',
    processor: '',
    
    // Step 4: Properties
    properties: [{ name: '', address: '', building_count: 1, unit_count: 1 }],
    
    // Step 5: User Registration
    user_first_name: '',
    user_last_name: '',
    user_email: '',
    user_phone: '',
    user_password: '',
    user_confirm_password: '',
    
    // Step 6: Pricing (calculated or from landing page calculator)
    pricing: location.state?.calculatedPricing || {
      setupFee: 0,
      monthlyFee: 0,
      transactionFee: 5,
      setup_fee: 0,
      monthly_fee: 0
    },

    // Promo/Referral Code (auto-filled from URL ref parameter)
    promo_code: ''
  });

  // Debounce email check - must be after formData declaration
  useEffect(() => {
    const email = formData.user_email;
    if (!email || email.length < 5) return;
    
    const timeoutId = setTimeout(() => {
      checkEmailAvailability(email);
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [formData.user_email]);

  // Auto-fill referral code from URL parameter
  useEffect(() => {
    if (refCode && !formData.promo_code) {
      setFormData(prev => ({ ...prev, promo_code: refCode }));
    }
  }, [refCode]);

  const caribbeanCountries = [
    { code: 'US', name: 'United States' },
    { code: 'AE', name: 'United Arab Emirates (Dubai)' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BZ', name: 'Belize' },
    { code: 'CU', name: 'Cuba' },
    { code: 'DM', name: 'Dominica' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'GD', name: 'Grenada' },
    { code: 'GY', name: 'Guyana' },
    { code: 'HT', name: 'Haiti' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'KN', name: 'Saint Kitts and Nevis' },
    { code: 'LC', name: 'Saint Lucia' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines' },
    { code: 'SR', name: 'Suriname' },
    { code: 'TT', name: 'Trinidad and Tobago' }
  ];

  const organizationTypes = [
    { 
      value: 'landlord', 
      label: 'Landlord', 
      description: 'Individual property owner managing rental units',
      icon: <Home className="h-6 w-6" />
    },
    { 
      value: 'hoa', 
      label: 'HOA Community', 
      description: 'Homeowners association managing community properties',
      icon: <Users className="h-6 w-6" />
    },
    { 
      value: 'property_manager', 
      label: 'Property Manager', 
      description: 'Professional managing multiple client properties',
      icon: <TrendingUp className="h-6 w-6" />
    },
    { 
      value: 'vacation_rental', 
      label: 'Vacation Rentals', 
      description: 'Short-term rental management with OTA channel sync',
      icon: <Palmtree className="h-6 w-6" />,
      badge: 'NEW'
    }
  ];

  const banks = [
    // Major US Banks (Top 20 by Assets) - Available for all countries
    { 
      name: 'JPMorgan Chase Bank', 
      swift: 'CHASUS33',
      countries: ['all']
    },
    { 
      name: 'Bank of America', 
      swift: 'BOFAUS3N',
      countries: ['all']
    },
    { 
      name: 'Wells Fargo', 
      swift: 'WFBIUS6S',
      countries: ['all']
    },
    { 
      name: 'Citibank', 
      swift: 'CITIUS33',
      countries: ['all']
    },
    { 
      name: 'U.S. Bank', 
      swift: 'USBKUS44',
      countries: ['all']
    },
    { 
      name: 'Goldman Sachs Bank USA', 
      swift: 'GSCMUS33',
      countries: ['all']
    },
    { 
      name: 'PNC Bank', 
      swift: 'PNCCUS33',
      countries: ['all']
    },
    { 
      name: 'Truist Bank', 
      swift: 'SNTRUS3A',
      countries: ['all']
    },
    { 
      name: 'Capital One', 
      swift: 'HIBKUS44',
      countries: ['all']
    },
    { 
      name: 'TD Bank', 
      swift: 'NRTHUS33',
      countries: ['all']
    },
    { 
      name: 'BMO Harris Bank', 
      swift: 'HATRUS44',
      countries: ['all']
    },
    { 
      name: 'Citizens Bank', 
      swift: 'CTZIUS33',
      countries: ['all']
    },
    { 
      name: 'Fifth Third Bank', 
      swift: 'FTBCUS3C',
      countries: ['all']
    },
    { 
      name: 'Huntington National Bank', 
      swift: 'HUNTUS33',
      countries: ['all']
    },
    { 
      name: 'M&T Bank', 
      swift: 'MNBDUS33',
      countries: ['all']
    },
    { 
      name: 'KeyBank', 
      swift: 'KEYBUS33',
      countries: ['all']
    },
    { 
      name: 'Regions Bank', 
      swift: 'ANBBUS33',
      countries: ['all']
    },
    // Caribbean Banks
    { 
      name: 'Bank of the Bahamas', 
      swift: 'BOBHBSNS',
      countries: ['BS']
    },
    { 
      name: 'Scotiabank', 
      swift: 'NOSCCATT',
      countries: ['JM', 'TT', 'BB', 'BS', 'GY', 'AG', 'KN', 'LC', 'VC', 'GD', 'DM']
    },
    { 
      name: 'Commonwealth Bank', 
      swift: 'CBTTBSNS',
      countries: ['BS', 'JM', 'TT', 'BB']
    },
    { 
      name: 'RBC Royal Bank', 
      swift: 'ROYCCAT2',
      countries: ['JM', 'TT', 'BB', 'BS']
    },
    { 
      name: 'CIBC FirstCaribbean', 
      swift: 'CIBCJMKX',
      countries: ['JM', 'TT', 'BB', 'BS', 'AG', 'KN', 'LC', 'VC', 'GD', 'DM']
    },
    { 
      name: 'Republic Bank', 
      swift: 'RBTTTTPS',
      countries: ['TT', 'GY', 'SR']
    },
    { 
      name: 'Jamaica National Bank', 
      swift: 'JNCBJMKX',
      countries: ['JM']
    },
    { 
      name: 'National Commercial Bank (Jamaica)', 
      swift: 'NBOFJMKX',
      countries: ['JM']
    },
    { 
      name: 'Other', 
      swift: '',
      countries: ['all']
    }
  ];

  const steps = [
    { number: 1, title: 'Organization Type', description: 'Choose your organization type' },
    { number: 2, title: 'Ownership', description: 'Business structure details' },
    { number: 3, title: 'Payment Setup', description: 'Banking and merchant accounts' },
    { number: 4, title: 'Properties', description: 'Add your properties' },
    { number: 5, title: 'User Registration', description: 'Create your account' },
    { number: 6, title: 'Pricing', description: 'Review pricing and confirm' }
  ];

  const progress = (currentStep / steps.length) * 100;

  // Get filtered banks based on selected country
  const getAvailableBanks = () => {
    return banks.filter(bank => 
      bank.countries.includes('all') || bank.countries.includes(formData.country)
    );
  };

  // Auto-fill SWIFT code when bank is selected
  const handleBankSelection = (bankName) => {
    const selectedBank = banks.find(bank => bank.name === bankName);
    setFormData({
      ...formData, 
      bank_name: bankName,
      swift_code: selectedBank?.swift || ''
    });
  };

  // Step 1: Organization Type and Country
  const renderStep1 = () => (
    <Card className="max-w-2xl mx-auto" data-testid="step-1-card">
      <CardHeader className="text-center">
        <Globe className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
        <CardTitle className="text-2xl">Welcome to Estate Flow</CardTitle>
        <p className="text-gray-600">Let's set up your property management account</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="org_name">Organization Name</Label>
          <Input
            id="org_name"
            placeholder="Enter your organization name"
            value={formData.org_name}
            onChange={(e) => setFormData({...formData, org_name: e.target.value})}
            data-testid="org-name-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Country</Label>
          <Select 
            value={formData.country} 
            onValueChange={(value) => setFormData({...formData, country: value})}
          >
            <SelectTrigger data-testid="country-select">
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent>
              {caribbeanCountries.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-4">
          <Label>Organization Type</Label>
          <RadioGroup 
            value={formData.org_type} 
            onValueChange={(value) => setFormData({...formData, org_type: value})}
            className="space-y-4"
          >
            {organizationTypes.map(type => (
              <div key={type.value} className="flex items-center space-x-2">
                <RadioGroupItem 
                  value={type.value} 
                  id={type.value}
                  data-testid={`org-type-${type.value}`}
                />
                <Label htmlFor={type.value} className="flex-1 cursor-pointer">
                  <div className={`flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 ${type.value === 'vacation_rental' ? 'border-emerald-300 bg-emerald-50' : ''}`}>
                    <div className={`mt-1 ${type.value === 'vacation_rental' ? 'text-emerald-600' : 'text-emerald-600'}`}>
                      {type.icon}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        {type.label}
                        {type.badge && (
                          <Badge className="bg-emerald-500 text-white text-xs">{type.badge}</Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">{type.description}</div>
                    </div>
                  </div>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  );

  // Step 2: Ownership Type
  const renderStep2 = () => (
    <Card className="max-w-2xl mx-auto" data-testid="step-2-card">
      <CardHeader className="text-center">
        <Building2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
        <CardTitle className="text-2xl">Ownership Structure</CardTitle>
        <p className="text-gray-600">How is your property ownership structured?</p>
      </CardHeader>
      <CardContent>
        <RadioGroup 
          value={formData.ownership_type} 
          onValueChange={(value) => setFormData({...formData, ownership_type: value})}
          className="space-y-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="personal" id="personal" data-testid="ownership-personal" />
            <Label htmlFor="personal" className="flex-1 cursor-pointer">
              <div className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="font-medium">Personal Name</div>
                <div className="text-sm text-gray-600">Property is owned under your personal name</div>
              </div>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="company" id="company" data-testid="ownership-company" />
            <Label htmlFor="company" className="flex-1 cursor-pointer">
              <div className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="font-medium">Company Name</div>
                <div className="text-sm text-gray-600">Property is owned by a registered business entity</div>
              </div>
            </Label>
          </div>
        </RadioGroup>
      </CardContent>
    </Card>
  );

  // Step 3: Enhanced Payment Setup with proper Wise API fields
  const renderStep3 = () => (
    <Card className="max-w-2xl mx-auto" data-testid="step-3-card">
      <CardHeader className="text-center">
        <CreditCard className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
        <CardTitle className="text-2xl">Payment Setup</CardTitle>
        <p className="text-gray-600">Configure your payment processing</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {formData.ownership_type === 'personal' ? (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900">Payout Account Setup</h4>
              <p className="text-sm text-blue-700">Enter the bank details where you'd like to receive your funds</p>
            </div>
            
            <div className="space-y-4">
              {/* Account Holder Name */}
              <div className="space-y-2">
                <Label htmlFor="account_holder_name">Account Holder Name</Label>
                <Input
                  id="account_holder_name"
                  placeholder="Full name as it appears on bank account"
                  value={formData.account_holder_name}
                  onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
                  data-testid="account-holder-name-input"
                />
              </div>

              {/* Bank Name */}
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Select 
                  value={formData.bank_name} 
                  onValueChange={handleBankSelection}
                >
                  <SelectTrigger data-testid="bank-select">
                    <SelectValue placeholder="Select your bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableBanks().map(bank => (
                      <SelectItem key={bank.name} value={bank.name}>{bank.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Show additional fields when "Other" is selected */}
              {formData.bank_name === 'Other' && (
                <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="custom_bank_name">Custom Bank Name</Label>
                    <Input
                      id="custom_bank_name"
                      placeholder="Enter your bank name"
                      data-testid="custom-bank-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_address">Bank Address</Label>
                    <Input
                      id="bank_address"
                      placeholder="Full bank address"
                      value={formData.bank_address}
                      onChange={(e) => setFormData({...formData, bank_address: e.target.value})}
                      data-testid="bank-address-input"
                    />
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="swift_code">SWIFT/BIC Code</Label>
                  <Input
                    id="swift_code"
                    placeholder="e.g., NOSCCATT"
                    value={formData.swift_code}
                    onChange={(e) => setFormData({...formData, swift_code: e.target.value})}
                    data-testid="swift-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="branch_code">Branch Code</Label>
                  <Input
                    id="branch_code"
                    placeholder="Branch/Sort code if required"
                    value={formData.branch_code}
                    onChange={(e) => setFormData({...formData, branch_code: e.target.value})}
                    data-testid="branch-code-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    id="account_number"
                    placeholder="Your account number"
                    value={formData.account_number}
                    onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                    data-testid="account-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="iban">IBAN (if applicable)</Label>
                  <Input
                    id="iban"
                    placeholder="International Bank Account Number"
                    value={formData.iban}
                    onChange={(e) => setFormData({...formData, iban: e.target.value})}
                    data-testid="iban-input"
                  />
                </div>
              </div>

              {/* Routing Number for US/Canada banks */}
              {['BS', 'BZ'].includes(formData.country) && (
                <div className="space-y-2">
                  <Label htmlFor="routing_number">Routing Number</Label>
                  <Input
                    id="routing_number"
                    placeholder="9-digit routing number"
                    value={formData.routing_number}
                    onChange={(e) => setFormData({...formData, routing_number: e.target.value})}
                    data-testid="routing-number-input"
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-4">
              <Label>How would you like to receive payments?</Label>
              <RadioGroup 
                value={formData.payment_preference || 'payout'} 
                onValueChange={(value) => setFormData({
                  ...formData, 
                  payment_preference: value,
                  has_merchant_account: value === 'existing_merchant'
                })}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="payout" id="payout_immediate" data-testid="payment-payout" />
                  <Label htmlFor="payout_immediate" className="cursor-pointer flex-1">
                    <div className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="font-medium">I want to receive payments right away</div>
                      <div className="text-sm text-gray-600">Get payouts directly to your bank account via our secure payment network. No merchant account needed.</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="existing_merchant" id="has_merchant" data-testid="has-merchant-yes" />
                  <Label htmlFor="has_merchant" className="cursor-pointer flex-1">
                    <div className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="font-medium">Yes, I have a merchant account</div>
                      <div className="text-sm text-gray-600">Lower transaction fees - integrate with your existing merchant account</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="need_merchant" id="no_merchant" data-testid="has-merchant-no" />
                  <Label htmlFor="no_merchant" className="cursor-pointer flex-1">
                    <div className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="font-medium">No, I need a merchant account</div>
                      <div className="text-sm text-gray-600">Lower long-term fees. We'll help you apply. You can switch from payouts later.</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {formData.payment_preference === 'payout' ? (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Payout Account Setup</h4>
                  <p className="text-sm text-blue-700 mb-3">Enter the bank details where you'd like to receive your funds. Payouts will be processed securely through our integrated payment network.</p>
                  <div className="flex items-start space-x-2 text-sm text-blue-700">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p>You can switch to a merchant account later for lower transaction fees</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Account Holder Name */}
                  <div className="space-y-2">
                    <Label htmlFor="hoa_account_holder_name">Account Holder Name</Label>
                    <Input
                      id="hoa_account_holder_name"
                      placeholder="HOA name or authorized person"
                      value={formData.account_holder_name}
                      onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
                      data-testid="hoa-account-holder-name-input"
                    />
                  </div>

                  {/* Bank Name */}
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <Select 
                      value={formData.bank_name} 
                      onValueChange={handleBankSelection}
                    >
                      <SelectTrigger data-testid="hoa-bank-select">
                        <SelectValue placeholder="Select your bank" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableBanks().map(bank => (
                          <SelectItem key={bank.name} value={bank.name}>{bank.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Show additional fields when "Other" is selected */}
                  {formData.bank_name === 'Other' && (
                    <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="space-y-2">
                        <Label htmlFor="hoa_custom_bank_name">Custom Bank Name</Label>
                        <Input
                          id="hoa_custom_bank_name"
                          placeholder="Enter your bank name"
                          value={formData.custom_bank_name}
                          onChange={(e) => setFormData({...formData, custom_bank_name: e.target.value})}
                          data-testid="hoa-custom-bank-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="hoa_bank_address">Bank Address</Label>
                        <Input
                          id="hoa_bank_address"
                          placeholder="Full bank address"
                          value={formData.bank_address}
                          onChange={(e) => setFormData({...formData, bank_address: e.target.value})}
                          data-testid="hoa-bank-address-input"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="hoa_swift_code">SWIFT/BIC Code</Label>
                      <Input
                        id="hoa_swift_code"
                        placeholder="e.g., NOSCCATT"
                        value={formData.swift_code}
                        onChange={(e) => setFormData({...formData, swift_code: e.target.value})}
                        data-testid="hoa-swift-code-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hoa_account_number">Account Number</Label>
                      <Input
                        id="hoa_account_number"
                        placeholder="Your account number"
                        value={formData.account_number}
                        onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                        data-testid="hoa-account-number-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : formData.payment_preference === 'existing_merchant' ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Bank for Merchant Account</Label>
                  <Select 
                    value={formData.bank_name} 
                    onValueChange={handleBankSelection}
                  >
                    <SelectTrigger data-testid="merchant-bank-select">
                      <SelectValue placeholder="Select your bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableBanks().map(bank => (
                        <SelectItem key={bank.name} value={bank.name}>
                          {bank.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Merchant ID</Label>
                  <Input
                    placeholder="Enter your merchant ID from your bank"
                    value={formData.merchant_id}
                    onChange={(e) => setFormData({...formData, merchant_id: e.target.value})}
                    data-testid="merchant-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SWIFT Code</Label>
                  <Input
                    placeholder="Bank SWIFT code"
                    value={formData.swift_code}
                    readOnly
                    className="bg-gray-50"
                    data-testid="swift-code-display"
                  />
                  <p className="text-xs text-gray-500">Auto-filled based on selected bank</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Bank for Merchant Account</Label>
                  <Select 
                    value={formData.bank_name} 
                    onValueChange={handleBankSelection}
                  >
                    <SelectTrigger data-testid="merchant-bank-select">
                      <SelectValue placeholder="Select your bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableBanks().map(bank => (
                        <SelectItem key={bank.name} value={bank.name}>{bank.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 mb-2">
                    We'll create a merchant application with your selected bank for approval. This process can take a few days.
                  </p>
                  <p className="text-sm text-yellow-800">
                    <strong>Want to start immediately?</strong> You can use the payout option above and switch to your merchant account once it's approved.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Step 4: Properties (same as before)
  const renderStep4 = () => (
    <Card className="max-w-4xl mx-auto" data-testid="step-4-card">
      <CardHeader className="text-center">
        <Building2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
        <CardTitle className="text-2xl">Add Your Properties</CardTitle>
        <p className="text-gray-600">Tell us about the properties you manage</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {formData.properties.map((property, index) => (
          <div key={index} className="p-6 border rounded-lg space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">Property {index + 1}</h4>
              {formData.properties.length > 1 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const newProperties = formData.properties.filter((_, i) => i !== index);
                    setFormData({...formData, properties: newProperties});
                  }}
                  data-testid={`remove-property-${index}`}
                >
                  Remove
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Property Name</Label>
                <Input
                  placeholder="e.g., Sunset Apartments"
                  value={property.name}
                  onChange={(e) => {
                    const newProperties = [...formData.properties];
                    newProperties[index].name = e.target.value;
                    setFormData({...formData, properties: newProperties});
                  }}
                  data-testid={`property-name-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  placeholder="Property address"
                  value={property.address}
                  onChange={(e) => {
                    const newProperties = [...formData.properties];
                    newProperties[index].address = e.target.value;
                    setFormData({...formData, properties: newProperties});
                  }}
                  data-testid={`property-address-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Number of Buildings</Label>
                <Select
                  value={String(property.building_count || 1)}
                  onValueChange={(value) => {
                    const newProperties = [...formData.properties];
                    newProperties[index].building_count = parseInt(value) || 1;
                    setFormData({...formData, properties: newProperties});
                  }}
                >
                  <SelectTrigger data-testid={`property-buildings-${index}`}>
                    <SelectValue placeholder="Select buildings" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,15,20,25,30,40,50,75,100].map(num => (
                      <SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'Building' : 'Buildings'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Number of Units</Label>
                <Select
                  value={String(property.unit_count || 1)}
                  onValueChange={(value) => {
                    const newProperties = [...formData.properties];
                    newProperties[index].unit_count = parseInt(value) || 1;
                    setFormData({...formData, properties: newProperties});
                  }}
                >
                  <SelectTrigger data-testid={`property-units-${index}`}>
                    <SelectValue placeholder="Select units" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,15,20,25,30,40,50,75,100,150,200,250,300,400,500].map(num => (
                      <SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'Unit' : 'Units'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
        
        <Button 
          variant="outline" 
          onClick={() => {
            const newProperties = [...formData.properties, { name: '', address: '', building_count: 1, unit_count: 1 }];
            setFormData({...formData, properties: newProperties});
          }}
          data-testid="add-property-btn"
        >
          Add Another Property
        </Button>
      </CardContent>
    </Card>
  );

  // Step 5: User Registration
  const renderStep5 = () => (
    <Card className="max-w-2xl mx-auto" data-testid="step-5-card">
      <CardHeader className="text-center">
        <User className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
        <CardTitle className="text-2xl">Create Your Account</CardTitle>
        <p className="text-gray-600">Set up your login credentials</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="user-first-name">First Name *</Label>
            <Input
              id="user-first-name"
              placeholder="Your first name"
              value={formData.user_first_name || ''}
              onChange={(e) => setFormData({...formData, user_first_name: e.target.value})}
              data-testid="user-first-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-last-name">Last Name *</Label>
            <Input
              id="user-last-name"
              placeholder="Your last name"
              value={formData.user_last_name || ''}
              onChange={(e) => setFormData({...formData, user_last_name: e.target.value})}
              data-testid="user-last-name"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="user-email">Email Address *</Label>
          <div className="relative">
            <Input
              id="user-email"
              type="email"
              placeholder="your@email.com"
              value={formData.user_email || ''}
              onChange={(e) => setFormData({...formData, user_email: e.target.value})}
              data-testid="user-email"
              className={emailValidation.available === false ? 'border-red-500 pr-10' : emailValidation.available === true ? 'border-green-500 pr-10' : ''}
            />
            {emailValidation.checking && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
              </div>
            )}
            {!emailValidation.checking && emailValidation.available === true && (
              <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
            )}
            {!emailValidation.checking && emailValidation.available === false && (
              <AlertTriangle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
            )}
          </div>
          {emailValidation.available === false && (
            <p className="text-sm text-red-500 mt-1">{emailValidation.message}</p>
          )}
          {emailValidation.available === true && (
            <p className="text-sm text-green-500 mt-1">{emailValidation.message}</p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="user-phone">Phone Number</Label>
          <Input
            id="user-phone"
            placeholder="Your phone number"
            value={formData.user_phone || ''}
            onChange={(e) => setFormData({...formData, user_phone: e.target.value})}
            data-testid="user-phone"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="user-password">Password *</Label>
          <Input
            id="user-password"
            type="password"
            placeholder="Create a secure password"
            value={formData.user_password || ''}
            onChange={(e) => setFormData({...formData, user_password: e.target.value})}
            data-testid="user-password"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="user-confirm-password">Confirm Password *</Label>
          <Input
            id="user-confirm-password"
            type="password"
            placeholder="Confirm your password"
            value={formData.user_confirm_password || ''}
            onChange={(e) => setFormData({...formData, user_confirm_password: e.target.value})}
            data-testid="user-confirm-password"
          />
        </div>
      </CardContent>
    </Card>
  );

  // Step 6: Trial Activation — No credit card required
  const renderStep6 = () => (
    <Card className="max-w-2xl mx-auto" data-testid="step-6-card">
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calculator className="h-8 w-8 text-emerald-600" />
        </div>
        <CardTitle className="text-2xl">Start Your 30-Day Free Trial</CardTitle>
        <p className="text-gray-600">Full access to all features. No credit card required.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center p-6 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg">
          <div className="text-5xl font-bold text-emerald-600 mb-2">30 Days Free</div>
          <p className="text-gray-600">Complete access to APC EstateFlow</p>
          <p className="text-sm text-gray-500 mt-2">No credit card required to start</p>
        </div>

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-3">Everything included in your trial:</h4>
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex items-center gap-2"><span className="text-emerald-600 font-bold">&#10003;</span> Complete property management platform</li>
            <li className="flex items-center gap-2"><span className="text-emerald-600 font-bold">&#10003;</span> Tenant portal with payment processing</li>
            <li className="flex items-center gap-2"><span className="text-emerald-600 font-bold">&#10003;</span> Financial reporting and analytics</li>
            <li className="flex items-center gap-2"><span className="text-emerald-600 font-bold">&#10003;</span> {formData.org_type === 'hoa' ? 'HOA governance, board documents, voting' : 'Maintenance request management'}</li>
            <li className="flex items-center gap-2"><span className="text-emerald-600 font-bold">&#10003;</span> Staff and role management (7 roles)</li>
            <li className="flex items-center gap-2"><span className="text-emerald-600 font-bold">&#10003;</span> Vendor portal with invoice uploads</li>
            <li className="flex items-center gap-2"><span className="text-emerald-600 font-bold">&#10003;</span> US, Canada & Caribbean support</li>
          </ul>
        </div>

        {/* Promo Code Section — REQUIRED */}
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <Label htmlFor="promo_code" className="text-purple-900 font-medium">
            Access Code (Required)
          </Label>
          <p className="text-xs text-purple-700 mb-2">A valid access code is required to start your trial. Contact your representative for a code.</p>
          <div className="flex space-x-2">
            <Input
              id="promo_code"
              placeholder="Enter your access code"
              value={formData.promo_code || ''}
              onChange={(e) => setFormData({...formData, promo_code: e.target.value.toUpperCase(), promo_verified: false})}
              className="flex-1 font-mono"
            />
            <Button
              variant="outline"
              onClick={async () => {
                if (!formData.promo_code) {
                  toast.error('Please enter an access code');
                  return;
                }
                try {
                  const response = await axios.post(`${API}/promo/validate`, {
                    code: formData.promo_code
                  });
                  if (response.data.valid) {
                    setFormData(prev => ({...prev, promo_verified: true}));
                    toast.success('Access code verified! You can now start your trial.', {
                      description: response.data.promo.description
                    });
                  } else {
                    setFormData(prev => ({...prev, promo_verified: false}));
                    toast.error('Invalid access code', {
                      description: response.data.message
                    });
                  }
                } catch (error) {
                  setFormData(prev => ({...prev, promo_verified: false}));
                  toast.error('Failed to validate access code');
                }
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Verify
            </Button>
          </div>
          {formData.promo_verified && (
            <p className="text-xs text-green-600 mt-2 font-medium">&#10003; Access code verified</p>
          )}
        </div>

        <div className="space-y-3 pt-4 border-t">
          <Button
            onClick={startFreeTrial}
            disabled={loading || !formData.promo_verified}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold disabled:opacity-50"
            size="lg"
            data-testid="start-trial-btn"
          >
            {loading ? 'Activating your trial...' : !formData.promo_verified ? 'Verify Access Code to Continue' : 'Start Free Trial — No Card Required'}
          </Button>
          
          <div className="text-center space-y-1">
            <p className="text-xs text-gray-500">
              Your trial will automatically activate with full access for 30 days.
            </p>
            <p className="text-xs text-gray-500">
              On Day 29, we'll ask you to add a payment method to continue.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // API calls (same as before but enhanced for new fields)
  const startOnboarding = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/onboarding/start`, {
        org_type: formData.org_type,
        country: formData.country,
        org_name: formData.org_name
      });
      const newOrgId = response.data.org_id;
      setOrgId(newOrgId);
      localStorage.setItem('onboarding_org_id', newOrgId);
      toast.success('Organization created successfully');
      return newOrgId; // Return for immediate use
    } catch (error) {
      console.error('Error starting onboarding:', error);
      toast.error(error.response?.data?.detail || 'Failed to start onboarding. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const saveOwnership = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/onboarding/${orgId}/ownership`, {
        ownership_type: formData.ownership_type
      });
      toast.success('Ownership details saved');
    } catch (error) {
      console.error('Error saving ownership:', error);
      toast.error('Failed to save ownership details');
    } finally {
      setLoading(false);
    }
  };

  const savePaymentDetails = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/onboarding/${orgId}/payment`, {
        has_merchant_account: formData.has_merchant_account,
        bank_name: formData.bank_name,
        bank_address: formData.bank_address,
        branch_code: formData.branch_code,
        swift_code: formData.swift_code,
        account_number: formData.account_number,
        account_holder_name: formData.account_holder_name,
        iban: formData.iban,
        routing_number: formData.routing_number,
        sort_code: formData.sort_code,
        merchant_id: formData.merchant_id,
        processor: formData.processor
      });
      toast.success('Payment details saved');
    } catch (error) {
      console.error('Error saving payment details:', error);
      toast.error('Failed to save payment details');
    } finally {
      setLoading(false);
    }
  };

  const calculatePricingForWizard = async () => {
    try {
      setLoading(true);
      
      // Calculate total units and buildings across all properties
      const totalUnits = formData.properties.reduce((sum, prop) => 
        sum + (parseInt(prop.unit_count) || 0), 0
      );
      const totalBuildings = formData.properties.reduce((sum, prop) => 
        sum + (parseInt(prop.building_count) || 1), 0
      );
      
      // Use SINGLE SOURCE OF TRUTH pricing engine
      const calculatedPricing = calculatePricing(formData.org_type, totalUnits, totalBuildings);
      
      // Log for debugging
      console.log('Pricing calculated using pricingEngine v' + PRICING_VERSION, {
        org_type: formData.org_type,
        totalUnits,
        totalBuildings,
        result: calculatedPricing
      });
      
      const pricingData = {
        setup_fee: calculatedPricing.setupFee,
        monthly_fee: calculatedPricing.monthlyFee,
        transaction_fee: calculatedPricing.transactionFee,
        total_units: totalUnits,
        total_buildings: totalBuildings,
        first_month_total: calculatedPricing.firstMonthTotal,
        pricing_version: PRICING_VERSION
      };
      
      // Try to save to backend, but don't fail if it doesn't work
      try {
        await axios.post(`${API}/onboarding/${orgId}/calculate-pricing`, {
          properties: formData.properties,
          pricing: pricingData
        });
      } catch (apiError) {
        console.warn('Failed to save pricing to backend, using local calculation:', apiError);
      }
      
      setFormData({...formData, pricing: pricingData});
      toast.success('Pricing calculated successfully');
    } catch (error) {
      console.error('Error calculating pricing:', error);
      // Use pricing engine for fallback too
      const fallbackPricing = calculatePricing(formData.org_type || 'landlord', 1, 1);
      setFormData({
        ...formData, 
        pricing: {
          setup_fee: fallbackPricing.setupFee,
          monthly_fee: fallbackPricing.monthlyFee,
          transaction_fee: fallbackPricing.transactionFee,
          total_units: 1,
          first_month_total: fallbackPricing.firstMonthTotal,
          pricing_version: PRICING_VERSION
        }
      });
      toast.warning('Using default pricing. Please verify before payment.');
    } finally {
      setLoading(false);
    }
  };

  const saveUserRegistration = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/onboarding/${orgId}/user-registration`, {
        user_first_name: formData.user_first_name,
        user_last_name: formData.user_last_name,
        user_email: formData.user_email,
        user_phone: formData.user_phone,
        user_password: formData.user_password
      });
      toast.success('User registration saved');
      return true; // Success
    } catch (error) {
      console.error('Error saving user registration:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save user registration';
      toast.error(errorMsg);
      return false; // Failed - block navigation
    } finally {
      setLoading(false);
    }
  };

  const initiatePayment = async () => {
    try {
      setLoading(true);
      setPaymentError(null);
      
      // Apply promo code if provided
      if (formData.promo_code) {
        try {
          const token = localStorage.getItem('access_token');
          const promoResponse = await axios.post(`${API}/promo/apply`, {
            code: formData.promo_code,
            organization_id: orgId
          }, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          
          if (promoResponse.data.success) {
            toast.success('Promo code applied successfully!', {
              description: 'Your discount has been activated'
            });
            
            // Check for test mode price override
            const isTestMode = promoResponse.data.is_test_mode;
            const setupFeeOverride = promoResponse.data.setup_fee_override;
            const monthlyFeeOverride = promoResponse.data.monthly_fee_override;
            
            if (isTestMode && setupFeeOverride !== undefined) {
              // Apply test mode pricing
              setFormData({
                ...formData,
                pricing: {
                  ...formData.pricing,
                  setup_fee: setupFeeOverride,
                  monthly_fee: monthlyFeeOverride || 0,
                  first_month_total: setupFeeOverride + (monthlyFeeOverride || 0),
                  is_test_mode: true
                }
              });
              toast.info(`TEST MODE: Pricing set to $${setupFeeOverride.toFixed(2)} setup + $${(monthlyFeeOverride || 0).toFixed(2)}/mo`, {
                duration: 5000
              });
            }
            
            // If it's a free subscription promo OR 100% discount, skip payment entirely
            const discountValue = promoResponse.data.discount_value || promoResponse.data.promo?.discount_value || 0;
            const discountType = promoResponse.data.discount_type || promoResponse.data.promo?.discount_type;
            
            if (discountType === 'free_subscription' || discountValue >= 100) {
              toast.success('FREE access granted! Completing setup...');
              try {
                const completeResponse = await axios.post(`${API}/onboarding/${orgId}/complete?start_trial=true`);
                
                if (completeResponse.data.access_token) {
                  localStorage.setItem('access_token', completeResponse.data.access_token);
                  localStorage.setItem('user_role', completeResponse.data.role);
                  localStorage.setItem('user_id', completeResponse.data.user_id);
                  localStorage.removeItem('onboarding_progress');
                  navigate(completeResponse.data.dashboard_url || '/dashboard');
                } else if (completeResponse.data.success) {
                  // Onboarding completed but no token — redirect to login
                  toast.success('Account setup complete! Please log in.');
                  localStorage.removeItem('onboarding_progress');
                  navigate('/login');
                }
                return;
              } catch (completeError) {
                console.error('Error completing onboarding with promo:', completeError);
                const errorMsg = completeError.response?.data?.detail || 'Failed to complete registration';
                toast.error(errorMsg);
                setPaymentError(errorMsg);
                return;
              }
            }
          }
        } catch (promoError) {
          console.error('Promo code error:', promoError);
          const errorMsg = promoError.response?.data?.detail || 'Promo code could not be applied';
          toast.error('Invalid promo code', { description: errorMsg });
          setPaymentError(`Promo code error: ${errorMsg}`);
          return;
        }
      }
      
      // Store orgId in localStorage before redirecting to Stripe
      localStorage.setItem('onboarding_org_id', orgId);
      
      // Get pricing values with fallbacks for different naming conventions
      const setupFee = formData.pricing.setup_fee || formData.pricing.setupFee || 0;
      const monthlyFee = formData.pricing.monthly_fee || formData.pricing.monthlyFee || 0;
      
      // Calculate total units and buildings for validation
      const totalUnits = formData.properties.reduce((sum, prop) => 
        sum + (parseInt(prop.unit_count) || 0), 0
      );
      const totalBuildings = formData.properties.reduce((sum, prop) => 
        sum + (parseInt(prop.building_count) || 1), 0
      );
      
      // PRICING VALIDATION GUARD - Ensure displayed pricing matches calculated pricing
      // Skip validation for test mode pricing
      const isTestMode = formData.pricing?.is_test_mode;
      
      if (!isTestMode) {
        const validation = validatePricing(
          { setupFee, monthlyFee },
          formData.org_type,
          totalUnits,
          totalBuildings
        );
        
        if (!validation.isValid) {
          console.error('PRICING MISMATCH BLOCKED!', validation);
          toast.error('Pricing validation failed. Please refresh the page and try again.', {
            description: 'If this persists, please contact support.'
          });
          setPaymentError('Pricing validation failed. The displayed price does not match our records. Please refresh the page or contact support.');
          return;
        }
        
        console.log('Pricing validation passed', {
          setupFee,
          monthlyFee,
          pricingVersion: PRICING_VERSION,
          validation
        });
      } else {
        console.log('TEST MODE - Skipping pricing validation', { setupFee, monthlyFee });
      }
      
      // Create Stripe checkout session
      const origin = window.location.origin;
      const response = await axios.post(`${API}/payments/create-checkout-session`, {
        org_id: orgId,
        setup_fee: setupFee,
        monthly_fee: monthlyFee,
        origin_url: origin,
        pricing_version: PRICING_VERSION  // Send pricing version for backend validation
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create payment session');
        setPaymentError('Failed to create payment session. Please try again.');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('Failed to initiate payment: ' + errorMsg);
      setPaymentError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/checkout-status/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return null;
    }
  };

  const startFreeTrial = async () => {
    try {
      setLoading(true);
      toast.info('Activating your 30-day free trial...');
      const response = await axios.post(`${API}/onboarding/${orgId}/complete?start_trial=true`);
      
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user_role', response.data.role);
        localStorage.setItem('user_id', response.data.user_id);
        
        toast.success('Your 30-day free trial is active! No credit card required. Redirecting to your dashboard...');
        
        localStorage.removeItem('onboarding_progress');
        localStorage.removeItem('onboarding_org_id');
        
        navigate(response.data.dashboard_url || '/dashboard');
      } else if (response.data.success) {
        toast.success('Trial activated! Please log in to get started.');
        localStorage.removeItem('onboarding_progress');
        localStorage.removeItem('onboarding_org_id');
        navigate('/login');
      }
    } catch (error) {
      console.error('Error starting free trial:', error);
      toast.error('Failed to start trial: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Navigation functions with progress saving
  const nextStep = async () => {
    setPaymentError(null); // Clear any previous errors
    
    if (currentStep === 1) {
      if (!formData.org_name || !formData.org_type || !formData.country) {
        toast.error('Please fill in all required fields');
        return;
      }
      const newOrgId = await startOnboarding();
      if (newOrgId) {
        await saveProgress(2);
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (!formData.ownership_type) {
        toast.error('Please select an ownership type');
        return;
      }
      await saveOwnership();
      await saveProgress(3);
      setCurrentStep(3);
    } else if (currentStep === 3) {
      await savePaymentDetails();
      await saveProgress(4);
      setCurrentStep(4);
    } else if (currentStep === 4) {
      if (formData.properties.some(p => !p.name || !p.address)) {
        toast.error('Please fill in all property details');
        return;
      }
      await saveProgress(5);
      setCurrentStep(5);
    } else if (currentStep === 5) {
      if (!formData.user_first_name || !formData.user_last_name || !formData.user_email || !formData.user_password) {
        toast.error('Please fill in all required user registration fields');
        return;
      }
      if (formData.user_password !== formData.user_confirm_password) {
        toast.error('Passwords do not match');
        return;
      }
      // Block if email validation shows it's not available
      if (emailValidation.available === false) {
        toast.error('This email is already registered. Please use a different email.');
        return;
      }
      const registrationSuccess = await saveUserRegistration();
      if (!registrationSuccess) {
        // Don't proceed if registration failed (e.g., email already registered)
        return;
      }
      await calculatePricingForWizard();
      setCurrentStep(6);
    } else if (currentStep === 6) {
      // Start free trial directly — no payment required
      await startFreeTrial();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return renderStep1();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 py-8 px-4">
      {/* Exit Intent Popup */}
      <ExitIntentPopup pageName="onboarding" />
      
      {/* Payment Error Banner */}
      {paymentError && (
        <div className="max-w-4xl mx-auto mb-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800">Payment Issue</h4>
              <p className="text-sm text-red-600">{paymentError}</p>
              <p className="text-xs text-red-500 mt-1">Please try again or use a different payment method.</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Building2 className="h-8 w-8 text-emerald-600" />
            <span className="text-2xl font-bold text-gray-900">Estate Flow</span>
          </div>
          <Badge variant="outline" className="bg-white">
            Step {currentStep} of {steps.length}
          </Badge>
        </div>
        
        {/* Progress bar */}
        <div className="mb-8">
          <Progress value={progress} className="h-2 mb-4" />
          <div className="flex justify-between text-sm text-gray-600">
            {steps.map((step, index) => (
              <div key={step.number} className={`text-center ${
                currentStep >= step.number ? 'text-emerald-600 font-medium' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full border-2 mx-auto mb-2 flex items-center justify-center ${
                  currentStep >= step.number 
                    ? 'border-emerald-500 bg-emerald-500 text-white' 
                    : 'border-gray-300'
                }`}>
                  {currentStep > step.number ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    step.number
                  )}
                </div>
                <div className="text-xs">{step.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-4xl mx-auto mb-8">
        {renderCurrentStep()}
      </div>

      {/* Navigation */}
      <div className="max-w-4xl mx-auto flex justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
          data-testid="prev-step-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>
        
        <Button
          onClick={nextStep}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="next-step-btn"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
          ) : (
            <>
              {currentStep === 6 ? 'Proceed to Payment' : currentStep === steps.length ? 'Complete Setup' : 'Continue'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default OnboardingWizard;
